import React, { useMemo } from "react";
import { Theme, StandardProps, Grid, Typography } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createStyles, makeStyles } from "@material-ui/styles";
import { StyleClassKey } from "js/react/typescript";
import { MediaGridComponent } from "js/react/scenes/components";
import {
    UIMediaContent,
    FileUIMediaContent,
    MediaContentRendererContainerProps
} from "js/react/scenes/components/MediaManagement/Types";
import Checkbox from "@material-ui/core/Checkbox";
import CheckIcon from "@material-ui/icons/Check";

import { black } from "material-ui/styles/colors";

const styles = (theme: Theme) =>
    createStyles({
        root: {},
        gridComponentWrapper: {
            height: "350px"
        },
        headerGrid: {
            color: "#b9babf",
            backgroundColor: "#f5f6fa",
            padding: "10px",
            marginBottom: "10px"
        },
        previousDirectoryWrapper: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            "&:hover": {
                cursor: "pointer"
            }
        },

        previousDirectoryTitle: {
            zIndex: 1,
            cursor: "pointer",
            color: theme.palette.common.black,
            opacity: 1,
            textAlign: "center"
        },

        levelUpAlt: {
            marginTop: "50px",
            color: theme.palette.primary.main,
            alignContent: "center",
            fontSize: "7rem",
            zIndex: 2
        },
        levelUpAltWrapper: {
            flexGrow: 1
        },
        checkmark: {
            padding: 0,
            position: "absolute",
            top: 0,
            left: "12px",
            height: "17px",
            width: "17px",
            backgroundColor: "transparent",
            borderRadius: "2px",
            border: "1px solid #9e9e9e"
        },
        tick: {
            padding: 0,
            position: "absolute",
            top: "0px",
            left: "11px",
            fontSize: "18px",
            color: "#9e9e9e"
        },
        checkboxSpan: {
            position: "relative"
        },
        checkbox: {
            opacity: 0,
            zIndex: 5
        }
    });

type MediaGridViewClassKey = StyleClassKey<typeof styles>;

const useStyles = makeStyles(styles);

export interface MediaGridViewProps
    extends StandardProps<{}, MediaGridViewClassKey>,
        MediaContentRendererContainerProps {}

function Component(props: MediaGridViewProps) {
    const classes = useStyles(props);
    const {
        objectListings,
        checkedValues,
        showPrevious,
        onPrevious,
        onCheckedValuesChange,

        ...rest
    } = props;
    const allIds = useMemo(() => objectListings.map(x => x.identifier), [
        objectListings
    ]);
    const allSelected =
        checkedValues.filter(x => allIds.includes(x.toString())).length ===
        allIds.length;
    const items = objectListings.map((f, idx) => {
        return (
            <Grid
                key={f.identifier}
                item
                sm={4}
                xs={6}
                className={classes.gridComponentWrapper}
            >
                <MediaGridComponent
                    key={f.identifier}
                    isChecked={checkedValues.includes(f.identifier)}
                    media={f}
                    {...rest}
                    onChecked={(f, checked) => {
                        if (checked) {
                            onCheckedValuesChange([
                                ...checkedValues
                                    .map(x => x.toString())
                                    .filter(x => x !== f.identifier),
                                f.identifier
                            ]);
                        } else {
                            onCheckedValuesChange(
                                checkedValues
                                    .map(x => x.toString())
                                    .filter(x => x !== f.identifier)
                            );
                        }
                    }}
                />
            </Grid>
        );
    });
    return (
        <React.Fragment>
            <Grid className={classes.headerGrid}>
                <span className={classes.checkboxSpan}>
                    <Checkbox
                        className={classes.checkbox}
                        checked={allSelected}
                        onClick={() => {
                            if (allSelected) {
                                onCheckedValuesChange([]);
                            } else {
                                onCheckedValuesChange(allIds);
                            }
                        }}
                    />
                    <span className={classes.checkmark}></span>
                    {allSelected && (
                        <span>
                            <CheckIcon className={classes.tick} />
                        </span>
                    )}
                </span>
                Name
            </Grid>
            <Grid container spacing={5} className={classes.root}>
                {/* Previous Directory*/}

                {showPrevious && (
                    <Grid
                        item
                        xs={4}
                        className={classes.previousDirectoryWrapper}
                        onClick={onPrevious}
                    >
                        <div className={classes.levelUpAltWrapper}>
                            <FontAwesomeIcon
                                icon={["fal", "level-up-alt"]}
                                className={classes.levelUpAlt}
                            />
                        </div>
                        <Typography
                            variant="body1"
                            className={classes.previousDirectoryTitle}
                        >
                            {"Previous Directory"}
                        </Typography>
                    </Grid>
                )}

                {/* Previous Directory*/}

                {items}
            </Grid>
        </React.Fragment>
    );
}

Component.defaultProps = {} as MediaGridViewProps;
Component.displayName = "MediaGridView";
export const MediaGridView = Component;
export default MediaGridView;
